import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useEntraAuth } from '../components/EntraAuth';
import SearchPopup from '../components/SearchPopup';
import RedirectOverlay from '../components/RedirectOverlay';
import logo from '../assets/Evoo_desktop_version_header.png';
import logoMobile from '../assets/Evoo_mobile_version_header.png';
import searchIcon from '../assets/search-svgrepo-com.svg';
import './Header.css';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [authType, setAuthType] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, logout: entraLogout } = useEntraAuth();

  // Check for authentication status
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const authTypeData = localStorage.getItem('authType');

    if (token && userData) {
      setUser(JSON.parse(userData));
      setAuthType(authTypeData);
    }
  }, []);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      // Show logout overlay
      setIsLoggingOut(true);

      // Small delay to show the overlay animation
      await new Promise(resolve => setTimeout(resolve, 500));

      // Clear local storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('authType');

      // Reset state
      setUser(null);
      setAuthType(null);

      // If using Entra ID, also logout from Entra with redirect
      if (authType === 'entra' && isAuthenticated) {
        await entraLogout('https://www.evoomedical.ro');
      } else {
        // For non-Entra users, redirect directly after showing overlay
        setTimeout(() => {
          window.location.href = 'https://www.evoomedical.ro';
        }, 1500);
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback redirect on error
      setTimeout(() => {
        window.location.href = 'https://www.evoomedical.ro';
      }, 1500);
    }
  };



  const handleDashboard = () => {
    if (user?.role === 'doctor') {
      navigate('/doctor-admin');
    } else {
      navigate('/');
    }
    closeMenu();
  };

  const handleSearchOpen = () => {
    setIsSearchOpen(true);
    closeMenu();
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
  };

  const handleHomeClick = () => {
    closeMenu();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <RedirectOverlay
        show={isLoggingOut}
        userName={user?.firstName || user?.first_name || 'utilizator'}
        message="Vă deconectăm și vă redirecționăm..."
        title="Deconectare reușită!"
      />
      <header className="header-container">
        <div className="header-flex-row">
          <div className="header-logo-section">
            <Link to="/" className="header-logo-link" onClick={closeMenu}>
              <img src={isMobile ? logoMobile : logo} alt="Evoo Medical Logo" className="header-logo" />
            </Link>
          </div>
          <nav className="header-nav">
            <div className="nav-links">
              <Link to="/" className="header-link" onClick={handleHomeClick}>Acasă</Link>



              <Link to="/specializari" className="header-link" onClick={closeMenu}>Specializări</Link>

              <Link to="/programari" className="header-link" onClick={closeMenu}>Programări</Link>

              <Link to="/medici" className="header-link" onClick={closeMenu}>Medici</Link>

              {/* Dropdown for Despre noi */}
              <div className="dropdown-container">
                <Link to="/despre-noi" className="header-link dropdown-toggle" onClick={closeMenu}>
                  Despre noi
                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                    <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
                <div className="dropdown-menu">
                  <Link to="/articole" className="dropdown-item" onClick={closeMenu}>Articole</Link>
                </div>
              </div>
              <button className="header-link" onClick={handleSearchOpen}>
                <img src={searchIcon} alt="Search" className="header-search-icon" />
                Caută
              </button>
              <Link to="/cariere" className="header-link" onClick={closeMenu}>Cariere</Link>
              <Link to="/login" className="header-link" onClick={closeMenu}>Contul meu</Link>
            </div>
          </nav>
          <button
            className="hamburger-menu"
            onClick={toggleMenu}
            aria-label="Toggle navigation menu"
          >
            <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
          </button>
        </div>

        {/* Search Popup */}
        <SearchPopup
          isOpen={isSearchOpen}
          onClose={handleSearchClose}
        />
      </header>

      {/* Mobile Navigation Overlay — must be OUTSIDE <header> to escape its
          stacking context (backdrop-filter on header traps fixed children) */}
      <div className={`nav-overlay ${isMenuOpen ? 'open' : ''}`} onClick={closeMenu}>
        <nav className="header-nav mobile-nav" onClick={(e) => e.stopPropagation()}>
          <Link to="/" className="header-link" onClick={handleHomeClick}>Acasă</Link>
          <Link to="/specializari" className="header-link" onClick={closeMenu}>Specializări</Link>
          <Link to="/programari" className="header-link" onClick={closeMenu}>Programări</Link>
          <Link to="/medici" className="header-link" onClick={closeMenu}>Medici</Link>
          <Link to="/despre-noi" className="header-link" onClick={closeMenu}>Despre noi</Link>
          <Link to="/cariere" className="header-link" onClick={closeMenu}>Cariere</Link>
          <Link to="/login" className="header-link" onClick={closeMenu}>Contul meu</Link>
          <Link to="/articole" className="header-link" onClick={closeMenu}>Articole</Link>
          <button className="header-link" onClick={handleSearchOpen}>
            <img src={searchIcon} alt="Search" className="header-search-icon" />
            Caută
          </button>

          {/* Mobile Authentication Buttons */}
          {user && (
            <div className="mobile-user-panel">
              <div className="user-info">
                <span className="user-greeting">
                  Bună, {user.firstName || user.first_name}!
                </span>
                <span className="user-role">
                  {user.role === 'doctor' ? 'Doctor' : 'Pacient'}
                </span>
              </div>
              {user.role === 'doctor' && (
                <button
                  className="header-link header-link--wide"
                  onClick={handleDashboard}
                >
                  Dashboard Medic
                </button>
              )}
              <button
                className="header-link header-link--logout"
                onClick={handleLogout}
              >
                Ieșire din cont
              </button>
            </div>
          )}
        </nav>
      </div>
    </>
  );
}

export default Header;
